import React, { CSSProperties, forwardRef, useEffect, useLayoutEffect } from "react";
import classNames from "classnames";

import { assignRef } from "./assignRef";
import { useBottomSheetContext } from "./bottomSheetContext";
import { useFocusTrap } from "./useFocusTrap";
import { useSheetGestures } from "./useSheetGestures";
import { useStartingStyle } from "./useStartingStyle";

const EXIT_FALLBACK_MS = 600;

export type PopupProps = React.HTMLAttributes<HTMLDivElement>;

export const Popup = forwardRef<HTMLDivElement, PopupProps>(
  ({ className, children, style, ...rest }, forwardedRef) => {
    const {
      open,
      setOpen,
      mounted,
      setMounted,
      dragging,
      titleId,
      descriptionId,
      popupRef,
      backdropRef,
      dismissible,
      modal,
      previousFocusRef,
      setDragging,
      snapPoints,
      setSnapPoint,
      activeSnapIndex,
      activeSnapOffset,
      keyboardOpen,
      nestedOpenCount,
      isNested,
      nestDepth,
      notifyParentNestedDrag,
      notifyParentNestedRelease,
    } = useBottomSheetContext();

    const startingStyle = useStartingStyle(open, popupRef);
    const hasSnapPoints = snapPoints !== null;
    const isExpanded =
      hasSnapPoints && activeSnapIndex === snapPoints.points.length - 1;
    const isFrontmost = nestedOpenCount === 0;

    useLayoutEffect(() => {
      if (!open) {
        if (popupRef.current) {
          popupRef.current.style.transform = "";
          popupRef.current.style.transition = "";
        }
        if (backdropRef.current) {
          backdropRef.current.style.opacity = "";
          backdropRef.current.style.transition = "";
        }
      }
    }, [open, popupRef, backdropRef]);

    useEffect(() => {
      if (open) {
        return;
      }

      let finished = false;
      function finishExit() {
        if (finished) {
          return;
        }
        finished = true;
        setMounted(false);
      }

      function onTransitionEnd(event: TransitionEvent) {
        if (event.target !== popupRef.current) {
          return;
        }
        if (event.propertyName !== "transform" && event.propertyName !== "opacity") {
          return;
        }
        finishExit();
      }

      const el = popupRef.current;
      el?.addEventListener("transitionend", onTransitionEnd);
      const timer = setTimeout(finishExit, EXIT_FALLBACK_MS);

      return () => {
        el?.removeEventListener("transitionend", onTransitionEnd);
        clearTimeout(timer);
      };
    }, [open, popupRef, setMounted]);

    useEffect(() => {
      if (!open || !dismissible || !isFrontmost) {
        return;
      }
      function onKeyDown(event: KeyboardEvent) {
        if (event.key === "Escape") {
          event.stopPropagation();
          setOpen(false, "escape-key");
        }
      }
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, dismissible, isFrontmost, setOpen]);

    useFocusTrap(popupRef, mounted && modal && isFrontmost, previousFocusRef, !open);

    useLayoutEffect(() => {
      const el = popupRef.current;
      if (!el) {
        return;
      }
      if (!isFrontmost) {
        el.setAttribute("inert", "");
      } else {
        el.removeAttribute("inert");
      }
    }, [isFrontmost, popupRef, mounted]);

    useSheetGestures({
      popupRef,
      backdropRef,
      open,
      dismissible,
      keyboardOpen,
      hasNestedOpen: !isFrontmost,
      snapOffsets: snapPoints?.offsets ?? [],
      snapPointValues: snapPoints?.points ?? [],
      activeSnapOffset,
      topSnapHeight: snapPoints?.topHeight ?? null,
      setDragging,
      onDismiss: () => setOpen(false, "swipe-down"),
      onSnapPointChange: setSnapPoint,
      onNestedDrag: notifyParentNestedDrag,
      onNestedRelease: notifyParentNestedRelease,
    });

    function setRefs(node: HTMLDivElement | null) {
      popupRef.current = node;
      assignRef(forwardedRef, node);
    }

    const snapStyle: CSSProperties = {
      ...(hasSnapPoints
        ? {
            height: snapPoints.topHeight,
            maxHeight: snapPoints.topHeight,
            minHeight: 0,
          }
        : null),
      ["--ui-bottom-sheet-snap-offset" as string]: `${activeSnapOffset}px`,
      zIndex: 10001 + nestDepth * 2,
    };

    return (
      <div
        {...rest}
        ref={setRefs}
        role="dialog"
        aria-modal={modal ? "true" : "false"}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={classNames("ui-bottom-sheet-dialog-popup", className)}
        style={{ ...snapStyle, ...style }}
        data-open={open ? "" : undefined}
        data-starting-style={startingStyle ? "" : undefined}
        data-dragging={dragging ? "" : undefined}
        data-snap-points={hasSnapPoints ? "" : undefined}
        data-snap-point={hasSnapPoints ? String(activeSnapIndex) : undefined}
        data-expanded={isExpanded ? "" : undefined}
        data-nested={isNested ? "" : undefined}
        data-behind={!isFrontmost ? "" : undefined}
      >
        <div className="ui-bottom-sheet-dialog-scroll">{children}</div>
      </div>
    );
  },
);

Popup.displayName = "BottomSheet.Popup";
